import Popover from "@mui/material/Popover";
import cn from "classnames";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import MoreVert from "assets/icons/MoreVertical.svg?react";

import TabGroupPanel from "pages/DevelopWorkspace/Editor/TabsBar/TabGroupPanel/TabGroupPanel";
import { useDocuments } from "pages/DevelopWorkspace/contexts/DocumentsContext/hooks/useDocuments";
import { TabGroup } from "pages/DevelopWorkspace/workspace.types";

import ContextMenu from "components/ContextMenu/ContextMenu";
import ContextMenuDivider from "components/ContextMenu/ContextMenuDivider";
import ContextMenuItem from "components/ContextMenu/ContextMenuItem";

import HintTooltip from "../components/HintTooltip";

import styles from "./TabGroupTab.module.scss";

interface TabGroupTabProps {
  tabGroup: TabGroup;
  memberTitles: string[];
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onUngroupAll: () => void;
  onDeleteGroup: () => void;
  onCreateTab: () => void;
}

// Max number of rows in HintTooltip
const MAX_TOOLTIP_ROWS = 10;

// Max number of characters in a row of HintTooltip
const MAX_TOOLTIP_ROW_CHARS = 47;

const toTooltipRow = (title: string) =>
  title.length > MAX_TOOLTIP_ROW_CHARS
    ? `${title.slice(0, MAX_TOOLTIP_ROW_CHARS - 1)}…`
    : title;

const TabGroupTab = (props: TabGroupTabProps) => {
  const {
    tabGroup,
    memberTitles,
    collapsed,
    onToggleCollapsed,
    onUngroupAll,
    onDeleteGroup,
    onCreateTab,
  } = props;

  const { t } = useTranslation();
  const {
    actions: { updateTabGroup },
  } = useDocuments();

  const titleRows = memberTitles.map(toTooltipRow);
  const tooltipContent = (
    titleRows.length > MAX_TOOLTIP_ROWS
      ? [
          ...titleRows.slice(0, MAX_TOOLTIP_ROWS - 1),
          t("tab_groups.tooltip.more_members", {
            count: titleRows.length - (MAX_TOOLTIP_ROWS - 1),
          }),
        ]
      : titleRows
  ).join("\n");

  const anchorEl = useRef<HTMLDivElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);

  const body = (
    <div className={styles.body}>
      <div className={styles.titleWrapper}>
        <div className={styles.title}>{tabGroup.name}</div>

        <div className={styles.gradientBoundaryRight} />
      </div>

      {collapsed && <div className={styles.count}>{memberTitles.length}</div>}

      <div className={styles.iconWrapper}>
        <div
          className={styles.menuIcon}
          onClick={event => {
            event.stopPropagation();
            setMenuOpen(true);
          }}
          data-testid={`tab-group-menu-icon-${tabGroup.name}`}
        >
          <MoreVert />
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={cn(styles.root, { [styles.menuOpened]: menuOpen })}
      onClick={onToggleCollapsed}
      ref={anchorEl}
      data-testid={`tab-group-${tabGroup.name}`}
    >
      <HintTooltip
        title={tabGroup.name}
        plainContent
        content={tooltipContent}
      >
        {body}
      </HintTooltip>

      {menuOpen && (
        <Popover
          open
          anchorEl={anchorEl.current}
          onClose={() => {
            setMenuOpen(false);
          }}
          onClick={event => {
            event.stopPropagation();
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <ContextMenu>
            <ContextMenuItem
              key="edit"
              text={t("tab_groups.menu.edit_group")}
              testId="edit-tab-group-item"
              onClick={event => {
                event.stopPropagation();
                setMenuOpen(false);
                setEditing(true);
              }}
            />

            <ContextMenuItem
              key="new-tab"
              text={t("tab_groups.menu.new_tab_in_group")}
              testId="new-tab-in-group-item"
              onClick={event => {
                event.stopPropagation();
                setMenuOpen(false);
                onCreateTab();
              }}
            />

            <ContextMenuItem
              key="collapse"
              text={
                collapsed
                  ? t("tab_groups.menu.expand_group")
                  : t("tab_groups.menu.collapse_group")
              }
              testId="collapse-tab-group-item"
              onClick={event => {
                event.stopPropagation();
                setMenuOpen(false);
                onToggleCollapsed();
              }}
            />

            <ContextMenuDivider key="divider" />

            <ContextMenuItem
              key="ungroup"
              text={t("tab_groups.menu.ungroup_all_tabs")}
              testId="ungroup-tab-group-item"
              onClick={event => {
                event.stopPropagation();
                setMenuOpen(false);
                onUngroupAll();
              }}
            />

            <ContextMenuItem
              key="delete"
              text={t("tab_groups.menu.delete_group")}
              testId="delete-tab-group-item"
              onClick={event => {
                event.stopPropagation();
                setMenuOpen(false);
                onDeleteGroup();
              }}
            />
          </ContextMenu>
        </Popover>
      )}

      {editing && (
        <TabGroupPanel
          anchorEl={anchorEl.current}
          attachTo="right"
          tabGroup={tabGroup}
          onClose={() => {
            setEditing(false);
            setMenuOpen(false);
          }}
          onSubmit={changes => {
            updateTabGroup(tabGroup.id, changes);
            setEditing(false);
            setMenuOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default TabGroupTab;
